import {
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { Icon } from '@iconify/react';

import { useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { ControlledTextField } from 'src/components/forms'
import { useLoadOptions } from 'src/hooks/useLoadOptions'
import { ICON_MAS } from 'src/pages/sistema/components/SharedIcons'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import CardActions from '../../../components/CardActions'
import { useLCSLHPTIConcentracionTipos, useLCSLHPTILimiteHoldingTipos, useLCSLHPTILimitePerdidaTipos, useLCSLHPTILimiteTipos } from '../hooks/useLCSLHPTICache'
import { LCSLHPTIGroupedHeader, LCSLHPTIRow, pivotLCSLHPTIData, useLCSLHPTITablaValorInicial } from '../hooks/useLCSLHPTITablaValor'
import { OperacionLCSLHPTIFields, OperacionLCSLHPTITablaValorFields } from '../types/fields'
import { FormOperacionLCSLHPTI } from '../types/types'
import TablaValorCeldaLCSLHPTI from './TablaValorCeldaLCSLHPTI'
import { remove } from 'nprogress'

const WIDTHS_BY_SELECTOR: Record<string, number> = {
  limite_credito: 140,
  limite_holding: 100,
  concentracion: 100,
  limite_perdida: 120,
  default: 130
};

interface SeccionNuevoLCSLHPTITablaValoresProps {
  formMethods: UseFormReturn<FormOperacionLCSLHPTI>
  codSistemaPrincipal: number
}

const SeccionNuevoLCSLHPTITablaValores = ({ formMethods, codSistemaPrincipal }: SeccionNuevoLCSLHPTITablaValoresProps) => {
  const [pivotData, setPivotData] = useState<{
    groupedHeaders: LCSLHPTIGroupedHeader[],
    rows: LCSLHPTIRow[],
    totalColumnas: number
  }>({ groupedHeaders: [], rows: [], totalColumnas: 0 });

  const { setValue, watch, control, getValues } = formMethods;
  const valoresEnForm = getValues(OperacionLCSLHPTIFields.TablaValores) || [];

  const {
    valores: tablaValorOriginal,
    isLoading,
  } = useLCSLHPTITablaValorInicial(codSistemaPrincipal);

  useEffect(() => {
    if (tablaValorOriginal.length > 0) {
      setValue(OperacionLCSLHPTIFields.TablaValores, tablaValorOriginal as any);

      // pivot manual al recibir la data original 
      const p = pivotLCSLHPTIData(tablaValorOriginal);
      setPivotData({
        groupedHeaders: p.groupedHeaders,
        rows: p.rows,
        totalColumnas: p.groupedHeaders.reduce((acc, g) => acc + g.columnas.length, 1)
      });
    }
  }, [tablaValorOriginal, setValue]);

  const { groupedHeaders, rows, totalColumnas } = pivotData;
  const filasReales = rows.filter(r => r.codInstrumento !== 15);
  const filasOtros = rows.filter(r => r.codInstrumento === 15);

  const { limiteTipos, isLoading: ldL, isError: erL, error: eL } = useLCSLHPTILimiteTipos();
  const { limiteHoldingTipos, isLoading: ldH, isError: erH, error: eH } = useLCSLHPTILimiteHoldingTipos();
  const { concentracionTipos, isLoading: ldC, isError: erC, error: eC } = useLCSLHPTIConcentracionTipos();
  const { limitePerdidaTipos, isLoading: ldP, isError: erP, error: eP } = useLCSLHPTILimitePerdidaTipos();

  const loadLimite = useLoadOptions({ data: limiteTipos, loading: ldL, isError: erL, error: eL });
  const loadHolding = useLoadOptions({ data: limiteHoldingTipos, loading: ldH, isError: erH, error: eH });
  const loadConcentracion = useLoadOptions({ data: concentracionTipos, loading: ldC, isError: erC, error: eC });
  const loadPerdida = useLoadOptions({ data: limitePerdidaTipos, loading: ldP, isError: erP, error: eP });

  // que selector va a usar cada campo
  const selectoresMapping = {
    limite_credito: loadLimite,
    limite_holding: loadHolding,
    concentracion: loadConcentracion,
    limite_perdida: loadPerdida,
  };

  useEffect(() => {
    if (tablaValorOriginal.length > 0) {
      setValue(OperacionLCSLHPTIFields.TablaValores, tablaValorOriginal as any);
    }
  }, [tablaValorOriginal, setValue]);


  const { formState: { errors } } = formMethods;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log("Errores de validación activos:", errors);
    }
  }, [errors]);

  // TODO: quitar esta funcion, es solo para rellenar la tabla y no tener que hacerlo 1 por 1
  const rellenarTablaAutomaticamente = () => {
    // primer valor de cada lista de opciones
    const valoresReferencia: Record<string, string> = {
      limite_credito: limiteTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      limite_holding: limiteHoldingTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      concentracion: concentracionTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
      limite_perdida: limitePerdidaTipos?.[0]?.[EntityWithIdAndDescriptionFields.Description] || "",
    };

    const valoresActuales = formMethods.getValues(OperacionLCSLHPTIFields.TablaValores);

    const nuevosValores = valoresActuales.map((celda: any) => {
      const valorAuto = valoresReferencia[celda.tipoSelector];
      if (valorAuto) {

        return {
          ...celda,
          [OperacionLCSLHPTITablaValorFields.Valor]: valorAuto
        };
      }

      return celda;
    });

    // settear todo en batch para que no re-renderize en cada setValue
    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: false
    });

    formMethods.clearErrors(OperacionLCSLHPTIFields.TablaValores);

  };

  const agregarFilaOtros = () => {
    const codOtros = 15;
    const nuevoRowId = `temp-${Date.now()}`;
    const valoresActuales = (getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];

    const nuevasCeldas = groupedHeaders.flatMap(group =>
      group.columnas.map(col => {
        const celdaReferencia = valoresActuales.find(v => v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo] === col.codOperacionTipo);

        return {
          [OperacionLCSLHPTITablaValorFields.CodInstrumento]: codOtros,
          [OperacionLCSLHPTITablaValorFields.DescInstrumento]: "Otros",
          [OperacionLCSLHPTITablaValorFields.CodOperacionTipo]: col.codOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.DescOperacionTipo]: col.descOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.Agrupacion]: group.agrupacion,
          [OperacionLCSLHPTITablaValorFields.Valor]: "",
          [OperacionLCSLHPTITablaValorFields.Orden]: 99,
          [OperacionLCSLHPTITablaValorFields.PermiteEditarInstrumento]: true,
          [OperacionLCSLHPTITablaValorFields.TipoSelector]: celdaReferencia?.[OperacionLCSLHPTITablaValorFields.TipoSelector] || "",
          rowId: nuevoRowId,
        };
      })
    );

    const nuevosValores = [...valoresActuales, ...nuevasCeldas];

    // actualizar formulario
    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });

    // actualizar data pivoteada
    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({
      groupedHeaders: p.groupedHeaders,
      rows: p.rows,
      totalColumnas: p.groupedHeaders.reduce((acc, g) => acc + g.columnas.length, 1)
    });
  };

  const eliminarFilaOtros = (rowKey: string | number) => {
    const actuales = getValues(OperacionLCSLHPTIFields.TablaValores) || [];

    const nuevosValores = actuales.filter((v: any) =>
      (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) !== rowKey
    );

    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });

    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({
      groupedHeaders: p.groupedHeaders,
      rows: p.rows,
      totalColumnas: p.groupedHeaders.reduce((acc, g) => acc + g.columnas.length, 1)
    });
  };

  const timerRef = useRef<any>(null);

  const renderRow = (row: LCSLHPTIRow) => {
    const indexNombre = (getValues(OperacionLCSLHPTIFields.TablaValores) as any[]).findIndex(v =>
      (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) === row.rowKey
    );

    return (
      <TableRow key={row.rowKey} hover>
        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', borderRight: 1, borderColor: 'divider', minWidth: 200 }}>
          {row.permiteEditarInstrumento ? (
            <Stack direction='row' alignItems='center'>
              <ControlledTextField
                // buscamos usando rowId que puede ser el instrumento o el id generado si es una fila de otros
                //@ts-ignore
                name={`${OperacionLCSLHPTIFields.TablaValores}.${indexNombre}.${OperacionLCSLHPTITablaValorFields.DescInstrumento}`}
                control={control}
                size="small"
                fullWidth
                onChangeCapture={(e: any) => {
                  const nuevoNombre = e.target.value;

                  // setvalue con debounce
                  if (timerRef.current) clearTimeout(timerRef.current);
                  timerRef.current = setTimeout(() => {
                    const actuales = getValues(OperacionLCSLHPTIFields.TablaValores);

                    actuales.forEach((item: any, idx: number) => {
                      const itemKey = item.rowId || item[OperacionLCSLHPTITablaValorFields.CodInstrumento];
                      if (itemKey === row.rowKey && idx !== indexNombre) { // no setValue en el textfield que estamos escribiendo
                        setValue(
                          `${OperacionLCSLHPTIFields.TablaValores}.${idx}.${OperacionLCSLHPTITablaValorFields.DescInstrumento}`,
                          nuevoNombre
                        );
                      }
                    });
                  }, 300);
                }}
              />
              <Tooltip title='Eliminar'>
                <IconButton size='small' onClick={() => eliminarFilaOtros(row.rowKey)}>
                  <Icon icon='tabler:trash' color={'red'} />
                </IconButton>
              </Tooltip>
            </Stack>

          ) : (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.descInstrumento}</Typography>
          )}
        </TableCell>

        {groupedHeaders.flatMap(group =>
          group.columnas.map(col => {
            const celda = row.celdas[col.codOperacionTipo];

            const indexEnArrayPlano = valoresEnForm.findIndex(v =>
              //@ts-ignore
              (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) === row.rowKey &&
              v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo] === col.codOperacionTipo
            );

            return (
              <TableCell key={`${row.rowKey}-${col.codOperacionTipo}`} align="center" sx={{
                borderRight: 1,
                borderColor: 'divider',
                minWidth: 110,
                width: 110,
                p: 0.5
              }}>
                {celda && indexEnArrayPlano !== -1 ? (
                  <TablaValorCeldaLCSLHPTI
                    control={control}
                    index={indexEnArrayPlano}
                    tipoSelector={celda[OperacionLCSLHPTITablaValorFields.TipoSelector]}
                    loadOptionsMapping={selectoresMapping}
                    permiteEditar={true}
                  />
                ) : (
                  <Typography variant="caption" color="text.disabled">-</Typography>
                )}
              </TableCell>
            );
          })
        )}
      </TableRow>
    )
  }


  return (
    <CardActions title="Matriz de Límites y Condiciones" icon='mdi:table-edit' colapsable tableContent>
      <Box sx={{ px: 4, pt: 3, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={rellenarTablaAutomaticamente}
        >
          Rellenar valores automáticamente
        </Button>
      </Box>
      {
        isLoading ?
          <Skeleton variant='rectangular' height={'600px'} />
          :

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                {/* agrupacion  */}
                <TableRow>
                  <TableCell
                    variant="head"
                    align="center"
                    sx={{ backgroundColor: 'grey.100', fontWeight: 'bold', borderRight: 1, borderColor: 'divider', fontSize: '0.75rem' }}
                  >
                    Instrumento
                  </TableCell>
                  {groupedHeaders.map((group) => (
                    <TableCell
                      key={group.agrupacion}
                      align="center"
                      colSpan={group.columnas.length}
                      sx={{ backgroundColor: 'grey.100', fontWeight: 'bold', borderRight: 1, borderColor: 'divider', fontSize: '0.1rem' }}
                    >
                      {group.agrupacion}
                    </TableCell>
                  ))}
                </TableRow>

                {/* tipo operacion */}
                <TableRow>
                  <TableCell sx={{ borderRight: 1, borderColor: 'divider' }} />
                  {groupedHeaders.flatMap(group =>
                    group.columnas.map(col => (
                      <TableCell
                        key={col.codOperacionTipo}
                        align="center"
                        sx={{
                          fontWeight: 'medium',
                          fontSize: '0.65rem',
                          borderRight: 1,
                          borderColor: 'divider',
                          p: 0.5,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1,
                          width: 110,
                          minWidth: 110
                        }}
                      >
                        {col.descOperacionTipo}
                      </TableCell>
                    ))
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* filas que no son "Otros" */}
                {filasReales.map((row) => renderRow(row))}
                {/* fila de agregar instrumento */}
                {filasReales.length > 0 &&
                  <TableRow>
                    <TableCell
                      sx={{
                        p: 0,
                        backgroundColor: 'grey.50',
                        borderRight: 1,
                        borderColor: 'divider',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                      }}
                    >

                      <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={agregarFilaOtros}
                        fullWidth
                        sx={{
                          justifyContent: 'flex-start',
                          borderRadius: 0,
                          fontSize: '0.75rem',
                        }}
                      >
                        Instrumento
                      </Button>
                    </TableCell>

                    {/* celdas vacias para rellenar */}
                    {groupedHeaders.flatMap(group =>
                      group.columnas.map(col => (
                        <TableCell
                          key={`spacer-${group.agrupacion}-${col.codOperacionTipo}`}
                          sx={{
                            backgroundColor: 'grey.50',
                            borderRight: 1,
                            borderColor: 'divider',
                            opacity: 0.5
                          }}
                        />
                      ))
                    )}
                  </TableRow>
                }


                {filasOtros.map((row) => renderRow(row))}
              </TableBody>
            </Table>
          </TableContainer>
      }
    </CardActions>
  )
}

export default SeccionNuevoLCSLHPTITablaValores;